import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Region } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { regionDisplayNames } from 'src/app/global/types';
import { TvdProgramsStore } from '../../stores/tvd-programs.store';
import {
  TvdByProgramDetailsData,
  TvdImpactCategory,
  TvdProgramDrillDownGrouping,
  TvdProgramLocationDetails,
  TvdProgramProductDetails,
  TvdProgramRegionDetails,
} from '../../types/tvd-types';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { SimplePageEvent } from 'src/app/core/types/paginator.types';

/**
 * Component for displaying detailed information about a specific Total Value Delivery (TVD) program,
 * including its performance by product and location, with filtering and pagination capabilities.
 */
@Component({
    selector: 'tvd-program-details-table',
    templateUrl: './tvd-program-details-table.component.html',
    styleUrls: ['./tvd-program-details-table.component.scss'],
    standalone: false
})
export class TvdProgramDetailsTableComponent implements OnInit, OnDestroy {
  @Input() selectedProgramName = '';

  // All fetched data rows for the selected program, before any filters are applied
  private _allProgramDetailsRows: TvdByProgramDetailsData[] = [];
  private _allProgramRegionRows: TvdProgramRegionDetails[] = [];

  // Subscriptions
  private _displayedCurrencyCodeSub?: Subscription;
  private _displayInRoundedNumberSub?: Subscription;
  private _currencyUomSub?: Subscription;
  private _programsCacheInvalidatedSub?: Subscription;

  selectedProductName = '';
  selectedProductCode = '';
  currentView: TvdProgramDrillDownGrouping =
    TvdProgramDrillDownGrouping.Product;

  // Filtered rows for display
  productTableRows: TvdProgramProductDetails[] = [];
  locationTableRows: TvdProgramLocationDetails[] = [];

  // Pagination state
  pagedProductTableRows: TvdProgramProductDetails[] = [];
  pagedLocationTableRows: TvdProgramLocationDetails[] = [];
  pageIndex = 0;
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 50];

  // Filter options
  availableRegions: Region[] = [];
  availableImpactCategories: TvdImpactCategory[] = [];
  selectedRegions: Region[] = [];
  selectedImpactCategories: TvdImpactCategory[] = [];

  // UI state
  isLoading = false;
  TvdProgramDrillDownGrouping = TvdProgramDrillDownGrouping;
  displayInRoundedNumber = false;
  currencyCode = 'USD';

  constructor(
    private _uiPreferencesService: UIPreferencesService,
    private _currencyUomService: CurrencyUomService,
    private _tvdProgramsStore: TvdProgramsStore,
  ) {}

  async ngOnInit(): Promise<void> {
    this._displayInRoundedNumberSub =
      this._uiPreferencesService.displayRoundedValuesObservable.subscribe(
        (f) => (this.displayInRoundedNumber = f),
      );

    this._displayedCurrencyCodeSub =
      this._currencyUomService.displayedCurrency$.subscribe(() => {
        this.currencyCode = this._currencyUomService.getDisplayedCurrency();
      });

    // Subscribe to cache invalidation triggers from the store to refresh data
    this._programsCacheInvalidatedSub =
      this._tvdProgramsStore.cacheInvalidated$.subscribe(async () => {
        this._currencyUomService.setCurrencyLoading(true);
        try {
          await this.initializeProgramDetailsTable(this.selectedProgramName);
        } finally {
          this._currencyUomService.setCurrencyLoading(false);
        }
      });

    // Initial load
    await this.initializeProgramDetailsTable(this.selectedProgramName);
  }

  ngOnDestroy(): void {
    this._displayInRoundedNumberSub?.unsubscribe();
    this._displayedCurrencyCodeSub?.unsubscribe();
    this._currencyUomSub?.unsubscribe();
    this._programsCacheInvalidatedSub?.unsubscribe();
  }

  /**
   * Initializes the program details by fetching data for the specified program name and setting up the component state.
   * If the program name is empty or only whitespace, it resets the page state to clear any displayed data.
   * Otherwise, it fetches the program details data from the TVD service, processes it to extract product and region details,
   * initializes the available filter options based on the fetched data, and applies any active filters to update the displayed rows.
   * @param programName program name selected by the user on TVD Program table on the landing page
   */
  async initializeProgramDetailsTable(programName: string): Promise<void> {
    if (!programName.trim()) {
      this.resetPageState();
      return;
    }

    this.isLoading = true;

    try {
      const response =
        await this._tvdProgramsStore.loadTvdProgramDetailsData(programName);
      const detailsRows = response ? [response] : [];

      this._allProgramDetailsRows = detailsRows;
      this._allProgramRegionRows = detailsRows.flatMap((row) =>
        row.programDetailsByRegion.map((regionDetails) => ({
          ...regionDetails,
          programDataByProduct: regionDetails.programDataByProduct.map(
            (product) => ({
              ...product,
              totalValueDelivered: {
                ...product.totalValueDelivered,
                value: product.totalValueDelivered.value,
              },
              impactCategories: this.parseImpactCategories(
                (product.impactCategories as unknown as string[]) ?? [],
              ),
              locations: (product.locations ?? []).map((location) => ({
                ...location,
                totalValueDelivered: {
                  ...location.totalValueDelivered,
                  value: location.totalValueDelivered.value,
                },
                impactCategories: this.parseImpactCategories(
                  (location.impactCategories as unknown as string[]) ?? [],
                ),
              })),
            }),
          ),
        })),
      );
      this.initializeFilterOptions();
      this.applyAllFilters();
    } catch {
      this.resetPageState();
    } finally {
      this.isLoading = false;
    }
  }

  //#region Filter handlers
  /**
   * Initializes the available filter options based on the fetched data.
   * This should be called whenever new data is loaded to ensure the filters reflect the current dataset.
   */
  private initializeFilterOptions(): void {
    this.availableRegions = [
      ...new Set(this._allProgramRegionRows.map((row) => row.region as Region)),
    ];

    this.availableImpactCategories = [
      ...new Set(
        this._allProgramRegionRows.flatMap((row) =>
          row.programDataByProduct.flatMap(
            (product) => product.impactCategories as TvdImpactCategory[],
          ),
        ),
      ),
    ];
  }

  /**
   * Applies the selected filters to the full dataset and updates the displayed rows accordingly.
   */
  applyAllFilters(): void {
    // First filter by regions, then update available impact categories based on that filtered set
    const regionFilteredRows = this.hasSelectedRegions()
      ? this._allProgramRegionRows.filter((regionRow) =>
          this.selectedRegions.includes(regionRow.region as Region),
        )
      : this._allProgramRegionRows;

    this.updateAvailableImpactCategories(regionFilteredRows);

    // Then flatten products and filter by impact categories
    const products = regionFilteredRows.flatMap((regionRow) =>
      regionRow.programDataByProduct.map((product) => ({
        ...product,
        locations: (product.locations ?? []).map((location) => ({
          ...location,
          region: regionRow.region,
        })),
      })),
    );

    const impactFilteredProducts = this.hasSelectedImpactCategories()
      ? products.filter((product) =>
          product.impactCategories.some((category) =>
            this.selectedImpactCategories.includes(category),
          ),
        )
      : products;

    // Finally, aggregate products to ensure uniqueness and sum values for duplicates
    this.productTableRows = this.aggregateProducts(impactFilteredProducts);

    // Update location rows based on the current view and selected product and then reset pagination
    this.updateLocationRowsForCurrentView();
    this.resetPaginationAndUpdatePagedRows();
  }

  /**
   * Updates the list of available impact categories based on the currently filtered regions.
   * This ensures that the impact category filter options are always relevant to the currently displayed data.
   * @param rows The currently region-filtered dataset to extract available impact categories from.
   */
  private updateAvailableImpactCategories(
    rows: TvdProgramRegionDetails[],
  ): void {
    this.availableImpactCategories = [
      ...new Set(
        rows.flatMap((regionRow) =>
          regionRow.programDataByProduct.flatMap(
            (product) => product.impactCategories as TvdImpactCategory[],
          ),
        ),
      ),
    ];

    this.selectedImpactCategories = this.selectedImpactCategories.filter(
      (category) => this.availableImpactCategories.includes(category),
    );
  }

  onRegionFilterChange(regions: Region[]): void {
    this.selectedRegions = regions;
    this.applyAllFilters();
  }

  onImpactCategoryFilterChange(categories: TvdImpactCategory[]): void {
    this.selectedImpactCategories = categories;
    this.applyAllFilters();
  }

  hasSelectedRegions(): boolean {
    return this.selectedRegions.length > 0;
  }

  hasSelectedImpactCategories(): boolean {
    return this.selectedImpactCategories.length > 0;
  }

  getRegionDisplayName(region: Region): string {
    return regionDisplayNames[region] ?? region;
  }

  private parseImpactCategories(raw: string[] = []): TvdImpactCategory[] {
    return raw
      .map((k) => TvdImpactCategory[k as keyof typeof TvdImpactCategory])
      .filter(Boolean) as TvdImpactCategory[];
  }
  //#endregion

  //#region Data aggregation
  /**
   * Aggregates the list of products to ensure that each product (identified by product code and name) appears only once,
   * summing their total value delivered and merging their impact categories and locations.
   * This is necessary because the raw data may contain multiple entries for the same product due to different regions or other factors,
   * and we want to present a clean, consolidated view in the product table.
   * @param products List of products to aggregate, which may contain duplicates based on product code and name.
   * @returns aggregated list of products with unique product code and name, where total value delivered is summed and impact categories and locations are merged.
   */
  private aggregateProducts(
    products: TvdProgramProductDetails[],
  ): TvdProgramProductDetails[] {
    const productsMap = new Map<string, TvdProgramProductDetails>();

    for (const product of products) {
      const key = `${product.productCode}|${product.productName}`;
      const existing = productsMap.get(key);

      if (!existing) {
        productsMap.set(key, {
          ...product,
          totalValueDelivered: {
            ...product.totalValueDelivered,
            value: product.totalValueDelivered.value,
          },
          impactCategories: [...new Set(product.impactCategories)],
          locations: [...(product.locations ?? [])],
        });
        continue;
      }

      existing.totalValueDelivered = {
        ...existing.totalValueDelivered,
        value:
          existing.totalValueDelivered.value +
          product.totalValueDelivered.value,
      };

      existing.impactCategories = [
        ...new Set([...existing.impactCategories, ...product.impactCategories]),
      ];

      existing.locations = [
        ...(existing.locations ?? []),
        ...(product.locations ?? []),
      ];
    }

    return Array.from(productsMap.values()).sort(
      (a, b) => b.totalValueDelivered.value - a.totalValueDelivered.value,
    );
  }

  /**
   * Aggregates all locations from the provided list of products into a single list, sorted by total value delivered.
   * This is used to generate the location table rows when the user is not currently viewing a specific product's locations.
   * It ensures that all locations across all displayed products are shown in a consolidated list, sorted by their contribution to total value delivered.
   * @param products A list of products from which to extract and aggregate location details. Each product may have its own list of locations, and this method will combine them into a single list.
   * @returns aggregated list of locations with total value delivered, sorted by their contribution to total value delivered.
   */
  private aggregateLocations(
    products: TvdProgramProductDetails[],
  ): TvdProgramLocationDetails[] {
    return products
      .flatMap((product) => {
        const locations = product.locations ?? [];
        return locations.map((location) => ({
          ...location,
          totalValueDelivered: {
            ...location.totalValueDelivered,
            value: location.totalValueDelivered.value,
          },
        }));
      })
      .sort(
        (a, b) => b.totalValueDelivered.value - a.totalValueDelivered.value,
      );
  }

  /**
   * This method updates the location table rows based on the current view and selected product.
   * If the current view is not grouped by location, it aggregates all locations from the currently displayed products.
   * If the current view is grouped by location and a product is selected, it displays only the locations for that product.
   * If no product is selected in the location view, it clears the location table rows.
   */
  private updateLocationRowsForCurrentView(): void {
    if (this.currentView !== TvdProgramDrillDownGrouping.Location) {
      this.locationTableRows = this.aggregateLocations(this.productTableRows);
      return;
    }
    if (!this.selectedProductCode) {
      this.locationTableRows = [];
      return;
    }

    const selectedProduct = this.productTableRows.find(
      (product) => product.productCode === this.selectedProductCode,
    );

    if (!selectedProduct) {
      this.locationTableRows = [];
      return;
    }

    this.selectedProductName = selectedProduct.productName;
    this.locationTableRows = [...(selectedProduct.locations ?? [])]
      .map((location) => ({
        ...location,
        totalValueDelivered: {
          ...location.totalValueDelivered,
          value: location.totalValueDelivered.value,
        },
      }))
      .sort(
        (a, b) => b.totalValueDelivered.value - a.totalValueDelivered.value,
      );
  }
  //#endregion

  //#region Pagination handlers
  onPageChange(event: SimplePageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedRowsForCurrentView();
  }

  get currentViewTotalItems(): number {
    return this.currentView === TvdProgramDrillDownGrouping.Location
      ? this.locationTableRows.length
      : this.productTableRows.length;
  }

  private resetPaginationAndUpdatePagedRows(): void {
    this.pageIndex = 0;
    this.updatePagedRowsForCurrentView();
  }

  /**
   * Shared method to update the paged rows for the current view based on the current page index and page size.
   * It calculates the correct slice of the full dataset (either products or locations) to display for the current page.
   * This method should be called whenever the page index, page size, or the underlying dataset changes to ensure the displayed rows are correct.
   * @returns paginated list of either product or location rows based on the current view, corresponding to the current page index and page size.
   */
  private updatePagedRowsForCurrentView(): void {
    const currentRows =
      this.currentView === TvdProgramDrillDownGrouping.Location
        ? this.locationTableRows
        : this.productTableRows;

    const maxPageIndex = Math.max(
      Math.ceil(currentRows.length / this.pageSize) - 1,
      0,
    );
    if (this.pageIndex > maxPageIndex) {
      this.pageIndex = maxPageIndex;
    }

    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;

    if (this.currentView === TvdProgramDrillDownGrouping.Location) {
      this.pagedLocationTableRows = this.locationTableRows.slice(start, end);
      return;
    }

    this.pagedProductTableRows = this.productTableRows.slice(start, end);
  }
  //#endregion

  //#region UI event handlers
  onProductLocationSelected(product: TvdProgramProductDetails): void {
    this.selectedProductName = product.productName;
    this.selectedProductCode = product.productCode;
    this.currentView = TvdProgramDrillDownGrouping.Location;
    this.updateLocationRowsForCurrentView();
    this.resetPaginationAndUpdatePagedRows();
  }

  navigateToProductsView(): void {
    this.currentView = TvdProgramDrillDownGrouping.Product;
    this.selectedProductName = '';
    this.selectedProductCode = '';
    this.resetPaginationAndUpdatePagedRows();
  }

  getUniqueLocationCount(row: TvdProgramProductDetails): number {
    return new Set((row.locations ?? []).map((location) => location.siteId))
      .size;
  }

  getDisplayValue(value: number): number {
    if (value == null) return 0;

    return Math.round(value);
  }

  private resetPageState(): void {
    this._allProgramDetailsRows = [];
    this._allProgramRegionRows = [];
    this.productTableRows = [];
    this.locationTableRows = [];
    this.availableRegions = [];
    this.availableImpactCategories = [];
    this.selectedRegions = [];
    this.selectedImpactCategories = [];
    this.selectedProductName = '';
    this.selectedProductCode = '';
    this.pagedProductTableRows = [];
    this.pagedLocationTableRows = [];
    this.pageIndex = 0;
  }
  //#endregion
}
